import { useState, useEffect } from 'react';

/**
 * Конвертує VAPID public key з base64url в Uint8Array
 * (необхідно для PushManager.subscribe)
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}

/**
 * navigator.serviceWorker.ready з timeout.
 * Якщо SW не зареєстрований (наприклад, /sw.js дає 404) — кидає помилку
 * замість того щоб висіти нескінченно.
 */
function swReady(timeoutMs = 10000): Promise<ServiceWorkerRegistration> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('[Push] Service Worker not ready: /sw.js may be missing or blocked')), timeoutMs)
    ),
  ]);
}

/**
 * C-1, C-6: Хук для управління Web Push підпискою.
 * - Перевіряє підтримку браузером (serviceWorker + PushManager)
 * - Перевіряє наявність vapidPublicKey в cabinetVars (не null)
 * - Використовує JWT Bearer token для авторизації на бекенді (M-6 fix)
 */
export function usePushNotifications() {
  const [isSupported, setIsSupported]   = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading]           = useState(true);
  // C-6: перевіряємо чи VAPID key налаштований
  const [vapidMissing, setVapidMissing] = useState(false);

  useEffect(() => {
    // C-6: перевіряємо vapidPublicKey — null означає не налаштований
    const vapidKey = (window as any).cabinetVars?.vapidPublicKey;
    if (!vapidKey) {
      setVapidMissing(true);
      setLoading(false);
      return;
    }

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      checkSubscription();
    } else {
      setLoading(false);
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration  = await swReady(5000);
      const subscription  = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (e) {
      console.error('[Push] Error checking subscription:', e);
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async (): Promise<boolean> => {
    try {
      setLoading(true);

      const vapidKey = (window as any).cabinetVars?.vapidPublicKey;
      if (!vapidKey) {
        console.warn('[Push] VAPID public key not configured');
        return false;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('[Push] Permission not granted');
        return false;
      }

      const registration  = await swReady(10000);
      const subscription  = await registration.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as unknown as BufferSource,
      });

      // M-6 fix: використовуємо Bearer JWT token замість X-WP-Nonce
      // щоб PushController (виправлений) коректно авторизував запит
      const token = sessionStorage.getItem('cab_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        // Fallback: nonce для випадку якщо JWT ще не ініціалізований
        headers['X-WP-Nonce'] = (window as any).cabinetVars?.nonce || '';
      }

      const wpApiUrl = (window as any).cabinetVars?.wpApiUrl || '/wp-json/api_front/v1';
      const res = await fetch(`${wpApiUrl}/push/subscribe`, {
        method:  'POST',
        headers,
        body: JSON.stringify(subscription.toJSON()),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error('[Push] Server error saving subscription:', errData);
        return false;
      }

      setIsSubscribed(true);
      return true;
    } catch (error) {
      console.error('[Push] Failed to subscribe:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async (): Promise<boolean> => {
    try {
      setLoading(true);
      const registration = await swReady(5000);
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();

        const token = sessionStorage.getItem('cab_token');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else {
          headers['X-WP-Nonce'] = (window as any).cabinetVars?.nonce || '';
        }

        const wpApiUrl = (window as any).cabinetVars?.wpApiUrl || '/wp-json/api_front/v1';
        await fetch(`${wpApiUrl}/push/unsubscribe`, {
          method:  'DELETE',
          headers,
          body: JSON.stringify({ endpoint }),
        });
      }

      setIsSubscribed(false);
      return true;
    } catch (error) {
      console.error('[Push] Failed to unsubscribe:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { isSupported, isSubscribed, loading, vapidMissing, subscribe, unsubscribe };
}
